import { get, startCase, toLower } from "lodash";
import { ConfigDefaults, ConfigOpretRetteDefaults } from "../constants/config";
import {
  PagedFirmaQueryField,
  FirmaPersonRelationerField,
  FirmaFirmaRelationField,
  FirmaKorrespondanceField,
  FirmaHistorikStamdataField,
  FirmaAbonnementerField,
  FirmaAltFaktureringsAdresserField,
  FirmaKlientErklaeringField,
  FirmaTilsynField,
  FirmaSanktionField,
  FirmaHistorikStamdata2Field,
} from "../interfaces/firma";
import { Firmaejerskab, PersonFirma } from "../interfaces/schema";
import { IColumn } from "../interfaces/table";
import { cvrNrStatusMapper } from "../util/cvrStatusMapper";

export const defaultFirmaColumns: IColumn<PagedFirmaQueryField>[] = [
  {
    field: "firmatype",
    title: "Firmatype",
    active: true,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        if ("firmatype" in rowData) {
          return startCase(
            toLower((rowData.firmatype as string).replaceAll("_", " ")),
          );
        }
      }
      return "";
    },
  },
  {
    field: "aktiv",
    title: "Status",
    active: true,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        if ("aktiv" in rowData) {
          return rowData.aktiv ? "Aktiv" : "Inaktiv";
        }
      }
      return "";
    },
  },
  { field: "firmaNr", title: "Firmanr.", active: true, format: "string" },
  { field: "navn1", title: "Navn 1", active: true, format: "string" },
  {
    field: "beliggenhedsadresse.adresselinie1",
    title: "Adresselinje 1",
    active: true,
    format: "string",
  },
  {
    field: "beliggenhedsadresse.adresselinie2",
    title: "Adresselinje 2",
    active: true,
    format: "string",
  },
  {
    field: "beliggenhedsadresse.postnummer",
    title: "Postnummer",
    active: true,
    format: "number",
  },
  {
    field: "beliggenhedsadresse.bynavn",
    title: "By",
    active: true,
    format: "string",
  },
  { field: "email", title: "Email", active: true, format: "string" },
  { field: "cvrNr", title: "CVR-nr.", active: true, format: "string" },
  { field: "pnr", title: "P-nr.", active: true, format: "string" },
  {
    field: "fakturaEmail",
    title: "Email (faktura)",
    active: false,
    format: "string",
  },
  {
    field: "emailSikker",
    title: "Email (sikker)",
    active: false,
    format: "string",
  },

  { field: "tlf", title: "Telefon", active: false, format: "string" },

  {
    field: "virksomhedsformNavigation.navn",
    title: "Virksomhedsform",
    active: false,
    format: "string",
  },
  {
    field: "selskabstypeNavigation.navn",
    title: "Selskabstype",
    active: false,
    format: "string",
  },
  {
    field: "retskredsNrNavigation.advokatkredsNrNavigation.navn",
    title: "Kreds",
    active: false,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        const data = get(
          rowData,
          "retskredsNrNavigation.advokatkredsNrNavigation.navn",
        );
        if (data) {
          return (data as string).replace("a", "A");
        }
      }
      return "";
    },
  },
  {
    field: "periode.fraDato",
    title: "Startdato",
    active: false,
    format: "date",
  },
  {
    field: "periode.tilDatoInklusiv",
    title: "Ophørsdato",
    active: false,
    format: "date",
  },
  {
    field: "antalAnsatteUdoevendeAdvokater",
    title: "Antal ansatte",
    active: false,
    format: "number",
  },
  {
    field: "debitorInfo.fakturaAfsendelse",
    title: "Afs.std.",
    active: false,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        const data = get(rowData, "debitorInfo.fakturaAfsendelse");
        if (data) {
          return startCase(toLower(data));
        }
      }
      return "";
    },
  },
  {
    field: "cvrNrStatusCvr",
    title: "CVR-status for virksomhed",
    active: false,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        if ("cvrNrStatusCvr" in rowData && rowData.cvrNrStatusCvr) {
          return (
            cvrNrStatusMapper.find(
              (status) => status.value === rowData.cvrNrStatusCvr,
            )?.displayName ?? ""
          );
        }
      }
      return "";
    },
  },
  {
    field: "pnrStatusCvr",
    title: "CVR-status for produktionsenhed",
    active: false,
    format: "string",
  },
  {
    field: "pnrOphoertCvr",
    title: "CVR ophørsdato for produktionsenhed",
    active: false,
    format: "date",
  },
  {
    field: "udenlandskVirksomhedsId",
    title: "Udenlandskvirksomheds ID",
    active: false,
    format: "number",
  },
];

export const defaultFirmaPersonRelationColumns: IColumn<FirmaPersonRelationerField>[] =
  [
    {
      field: "person.kundeNr",
      title: "Kundenr.",
      active: true,
      format: "string",
      link: (personFirma: PersonFirma) => `/person/${personFirma.person.id}`,
    },
    {
      field: "person.fornavne",
      title: "Navn",
      active: true,
      format: "string",
      link: (personFirma: PersonFirma) => `/person/${personFirma.person.id}`,
    },
    {
      field: "person.efternavn",
      active: true,
      format: "string",
      title: "Efternavn",
    },
    {
      field: "personfirmatypeNrNavigation.navn",
      title: "Relation",
      active: true,
      format: "string",
    },
    {
      field: "person.personart",
      title: "Personart",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      active: true,
      format: "date",
      title: "Startdato",
    },
    {
      field: "periode.tilDatoInklusiv",
      active: true,
      format: "date",
      title: "Slutdato",
    },
    { field: "emailArbejde", title: "Email", active: true, format: "string" },
    ...ConfigDefaults,
  ];
export const defaultEjendeFirmaRelationColumns: IColumn<FirmaFirmaRelationField>[] =
  [
    // { field: "id", title: "Id", active: false, format: "string" },
    {
      field: "ejendeFirmaNavigation.firmaNr",
      title: "Firmanr.",
      active: true,
      format: "string",
    },
    {
      field: "ejendeFirmaNavigation.navn1",
      title: "Navn",
      active: true,
      format: "string",
      link: (firma: Firmaejerskab) =>
        `/firma/${firma.ejendeFirmaNavigation.id}`,
    },
    {
      field: "ejendeFirmaNavigation.cvrNr",
      title: "CVR-nummer",
      active: true,
      format: "string",
    },
    {
      field: "ejendeFirmaNavigation.pnr",
      title: "P-nummer",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "ejendeFirmaNavigation.id",
      title: "Id",
      active: false,
      format: "string",
    },
    ...(ConfigOpretRetteDefaults as any),
  ];

export const defaultEjetFirmaRelationColumns: IColumn<FirmaFirmaRelationField>[] =
  [
    // { field: "id", title: "Id", active: false, format: "string" },
    {
      field: "ejetFirma.firmaNr",
      title: "Firmanr.",
      active: true,
      format: "string",
    },
    {
      field: "ejetFirma.navn1",
      title: "Navn",
      active: true,
      format: "string",
      link: (firma: Firmaejerskab) => `/firma/${firma.ejetFirma.id}`,
    },
    {
      field: "ejetFirma.cvrNr",
      title: "CVR-nummer",
      active: true,
      format: "string",
    },
    {
      field: "ejetFirma.pnr",
      title: "P-nummer",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "ejetFirma.id",
      title: "Id",
      active: false,
      format: "string",
    },
    ...(ConfigOpretRetteDefaults as any),
  ];

export const defaultFirmaKorrespondanceColumns: IColumn<FirmaKorrespondanceField>[] =
  [
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: true, format: "date" },
    {
      field: "beskrivelse",
      title: "Beskrivelse",
      active: true,
      format: "string",
    },
    { field: "navn", title: "Beskrivelse2", active: true, format: "string" },
    { field: "filnavn", title: "Filtype", active: true, format: "string" },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: true,
      format: "string",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },

    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "dokumentNr",
      title: "Dokument nr",
      active: false,
      format: "number",
    },
  ];

export const defaultFirmaHistorikstamdataColumns: IColumn<FirmaHistorikStamdataField>[] =
  [
    { field: "navn1", title: "Navn", active: true, format: "string" },
    { field: "cvrNr", title: "CVR-nr:", active: true, format: "string" },
    { field: "pnr", title: "P-nr:", active: true, format: "string" },
    { field: "aktiv", title: "Aktiv", active: true, format: "string" },
    { field: "adr1", title: "Adresselinje 1", active: false, format: "string" },
    { field: "adr2", title: "Adresselinje 2", active: false, format: "string" },
    { field: "adr3", title: "Adresselinje 3", active: false, format: "string" },
    { field: "adr4", title: "Adresselinje 4", active: false, format: "string" },
    { field: "husNr", title: "Husnr.", active: false, format: "string" },
    {
      field: "husNrBogstav",
      title: "Bogstav",
      active: false,
      format: "string",
    },
    { field: "etageNr", title: "Etage", active: false, format: "string" },
    { field: "side", title: "Side", active: false, format: "string" },
    { field: "postNr", title: "Postnr.", active: false, format: "string" },
    { field: "postboks", title: "Postboks", active: false, format: "string" },
    { field: "emailArbejde", title: "E-mail", active: false, format: "string" },
    { field: "tlf", title: "Telf.", active: false, format: "string" },
    { field: "firmatype", title: "Firmatype", active: false, format: "string" },
    { field: "kredsNavn", title: "Kreds", active: false, format: "string" },
    {
      field: "firmaStartDato",
      title: "Startdato",
      active: false,
      format: "string",
    },
    { field: "kommuneNavn", title: "Kommune", active: false, format: "string" },
    { field: "land", title: "Land", active: false, format: "string" },
    { field: "regionNavn", title: "Region", active: false, format: "string" },
    ...ConfigDefaults,
  ];

export const defaultFirmaHistorikstamdata2Columns: IColumn<FirmaHistorikStamdata2Field>[] =
  [
    {
      field: "snapshotTid",
      title: "Snapshot Tid",
      active: true,
      format: "date",
    },
    { field: "id", title: "ID", active: true, format: "string" },
    { field: "firmaNr", title: "Firmanr.", active: true, format: "number" },
    {
      field: "advokatvirksomhed",
      title: "Advokatvirksomhed",
      active: true,
      format: "boolean",
    },
    { field: "cvrNr", title: "CVR-nr.", active: true, format: "number" },
    { field: "pnr", title: "P-nr.", active: true, format: "number" },
    {
      field: "virksomhedsform",
      title: "Virksomhedsform",
      active: true,
      format: "string",
    },
    {
      field: "adresseAdresselinie1",
      title: "Adresselinie 1",
      active: true,
      format: "string",
    },
    {
      field: "adresseAdresselinie2",
      title: "Adresselinie 2",
      active: true,
      format: "string",
    },
    {
      field: "adresseAdresselinie4",
      title: "Adresselinie 4",
      active: true,
      format: "string",
    },
    {
      field: "adressePostnummer",
      title: "Postnr.",
      active: true,
      format: "number",
    },
    { field: "adresseBynavn", title: "By", active: true, format: "string" },
    {
      field: "adresseLandekode",
      title: "Landekode",
      active: true,
      format: "string",
    },
    { field: "kommune", title: "Kommune", active: true, format: "string" },
    { field: "region", title: "Region", active: true, format: "string" },
    { field: "retskreds", title: "Retskreds", active: true, format: "string" },
    { field: "tlf", title: "Telefon", active: true, format: "number" },
    { field: "email", title: "Email", active: true, format: "string" },
    {
      field: "emailSikker",
      title: "Email (sikker)",
      active: true,
      format: "string",
    },
    {
      field: "webAdresse",
      title: "Hjemmeside",
      active: true,
      format: "string",
    },
    {
      field: "periodeFraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periodeTilDato",
      title: "Ophørsdato",
      active: true,
      format: "date",
    },
  ];

export const defaultFirmaAbonnementerColumns: IColumn<FirmaAbonnementerField>[] =
  [
    {
      field: "abonnementNrNavigation.navn",
      title: "Abonnement",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Start dato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slut dato",
      active: true,
      format: "date",
    },
    {
      field: "faktureres",
      title: "Faktureres",
      active: true,
      format: "boolean",
    },
    { field: "antalEksemplar", title: "Antal", active: true, format: "string" },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    { field: "retteInit", title: "Rettet af", active: false, format: "string" },
    {
      field: "sidsteOpkraevningsaar",
      title: "Sidste opkrævningsår",
      active: false,
      format: "string",
    },
  ];

export const defaultFirmaAltFaktureringsAdresserColumns: IColumn<FirmaAltFaktureringsAdresserField>[] =
  [
    {
      field: "adresse.adresselinie1",
      active: true,
      format: "string",
      title: "Adresselinje 1",
    },
    {
      field: "adresse.adresselinie2",
      active: false,
      format: "string",
      title: "Adresselinje 2",
    },
    {
      field: "adresse.postnummer",
      active: true,
      format: "string",
      title: "Postnummer",
    },
    {
      field: "adresse.bynavn",
      active: true,
      format: "string",
      title: "Bynavn",
    },
    {
      field: "adresse.adresselinie4",
      active: false,
      format: "string",
      title: "Adresselinje 4",
    },
    {
      field: "adresse.landNavigation.navn",
      active: true,
      format: "string",
      title: "Land",
    },
    {
      field: "email",
      active: true,
      format: "string",
      title: "Email",
    },
    {
      field: "adresse.coNavn",
      active: true,
      format: "string",
      title: "C/O",
    },
    {
      field: "bemaerkning",
      active: true,
      format: "string",
      title: "Bemærkning",
    },
    {
      field: "periode.fraDato",
      active: true,
      format: "date",
      title: "Startdato",
    },
    {
      field: "periode.tilDatoInklusiv",
      active: true,
      format: "date",
      title: "Slutdato",
    },
    {
      field: "reference",
      active: true,
      format: "string",
      title: "Reference",
    },
    ...ConfigDefaults,
  ];

export const defaultFirmaKlienterklaeringColumns: IColumn<FirmaKlientErklaeringField>[] =
  [
    { field: "erklAar", title: "Erklæringsår", active: true, format: "number" },
    {
      field: "klienterklaeringstypeNavigation.navn",
      title: "Type",
      active: true,
      format: "string",
    },
    { field: "firmaCvrNr", title: "CVR-nr.", active: true, format: "string" },
    {
      field: "klienterklaeringStatusNavigation.navn",
      title: "Status",
      active: true,
      format: "string",
    },
    {
      field: "erklModtaget",
      title: "Modtaget dato",
      active: true,
      format: "date",
    },
    {
      field: "erklNrWeb",
      title: "Nr. weberklæring",
      active: true,
      format: "number",
    },
    {
      field: "erklNr",
      title: "Nr. analog erklæring",
      active: true,
      format: "number",
    },
    {
      field: "frist",
      title: "Frist",
      active: false,
      format: "date",
    },
    {
      field: "koerselDato",
      title: "Kørsel dato",
      active: false,
      format: "date",
    },
    {
      field: "periode.fraDato",
      title: "Startdato erklæringsperiode",
      active: false,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato erklæringsperiode",
      active: false,
      format: "date",
    },
    ...ConfigDefaults,
  ];

export const defaultFirmaTilsynFieldColumns: IColumn<FirmaTilsynField>[] = [
  {
    field: "gennemfoerselDato",
    title: "Gennemført",
    active: true,
    format: "date_quarter",
  },
  {
    field: "firmaTilsynstype",
    title: "Type",
    active: true,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "string" && rowData !== null) {
        const titleCase =
          rowData.charAt(0).toLocaleUpperCase("da-DK") +
          rowData.slice(1).toLocaleLowerCase("da-DK");
        const displayName = titleCase
          .replaceAll("ae", "æ")
          .replaceAll("oe", "ø");
        return displayName;
      }
      return "";
    },
  },
  {
    field: "sagsNr",
    title: "Sagsnr. F2",
    active: true,
    format: "string",
    externalLink: (rowData) => rowData.sagsNr,
  },
  {
    field: "bemaerkning",
    title: "Bemærkning",
    active: true,
    format: "string",
  },
  ...ConfigDefaults,
];

export const defaultFirmaSanktionFieldColumns: IColumn<FirmaSanktionField>[] = [
  { field: "sagsNr", title: "Sagsnr.", active: true, format: "string" },
  {
    field: "naevnskendelseDato",
    title: "Kendelsesdato",
    active: true,
    format: "date",
  },
  {
    field: "naevnskendelseSanktionstypeNrNavigation.navn",
    title: "Sanktion (nævn)",
    active: true,
    format: "string",
  },
  {
    field: "naevnskendelseBoedebeloeb",
    title: "Bødebeløb (nævn)",
    active: true,
    format: "string",
  },
  {
    field: "indbragtretten",
    title: "Indbragt",
    active: true,
    format: "boolean",
  },
  {
    field: "retskendelseSanktionstypeNrNavigation.navn",
    title: "Sanktion (retten)",
    active: true,
    format: "string",
  },
  {
    field: "retskendelseBoedebeloeb",
    title: "Bødebeløb (retten)",
    active: true,
    format: "string",
  },
  {
    field: "bemaerkninger",
    title: "Bemærkninger",
    active: true,
    format: "string",
  },
  {
    field: "sagsLink",
    title: "Kendelse",
    active: true,
    format: "string",
    externalLink: (rowdata) => rowdata.sagsLink,
  },
  {
    field: "afgoerelseskoder",
    title: "Afgørelseskoder",
    active: true,
    format: "string",
  },
  ...ConfigDefaults,
];
